/**
 * @module behaviors
 *
 * All the things that the app *does*. The business logic.
 *
 * Anything that the user can cause the app to do, this is where it happens. The
 * view should do nothing more than capture the event, extract the data, and
 * then dispatch one or more of these action creators. The action creators are
 * what do the actual business logic. The consequences of any given event should
 * be defined here.
 *
 * Some other aspects of "behavior" are also found here, such as the reducers
 * (in charge of controlling the shape of the state's data given the results of
 * a given action). Others, like the normalization schemas, are used internally
 * to format data into and out of the store in a consistent way. The selectors
 * are used at the other end for when the view wants to request a particular
 * shape of data from the state's store.
 *
 * This is also where the Redux definition lives. It is completely isolated as
 * a custom React context and connected to the store/reducers here and exported
 * as a provider, a context, and a set of hooks for inclusion by the root
 * React component. It actually makes it easier to work with since whenever
 * you want to use an action, you also will require the `useDispatch` hook, so
 * now they're both conveniently exported from the same package.
 *
 * NOTE: The American spelling of behaviors was chosen (without a "u") to be
 * consistent with other keyword spellings (e.g., "color" in CSS) so that it
 * is easy to know which spelling to use.
 */
import { ReduxContext } from './ReduxContext';
import { ReduxProvider } from './ReduxProvider';
import * as transformers from './transformers';
import * as languages from './languages';
import { useAppSelector, useAppDispatch, useStore } from './redux';
import { store, rootReducer, combineMiddlewares } from './store';
import DatePickerProvider from './date_picker.provider';

const useTranslation = languages.useTranslation;

// REFACTOR: can the "hooks" be grouped together into their own pseudo-module?
// Should *all* hooks be defined in this central location? (e.g., react-router.useHistory?)
export {
  transformers,
  languages,
  ReduxProvider,
  ReduxContext,
  useAppSelector,
  useAppDispatch,
  useStore,
  useTranslation,
  store,
  rootReducer,
  combineMiddlewares,
  DatePickerProvider,
};
